import { RegionPathPopoverContent } from '@/components/RegionDisplay';
import {
  getEnabledCountryLocationData,
  getLocationDisplayName,
} from '@/services/admin/locationData';
import type { TreeSelectProps } from 'antd';
import { Popover, TreeSelect } from 'antd';
import React, { useEffect, useState } from 'react';

export type RegionSelectAllowLevel = 'any' | 'leaf';

export interface RegionSelectProps
  extends Omit<
    TreeSelectProps,
    'treeData' | 'loading' | 'treeLine' | 'placeholder'
  > {
  /** 是否多选（默认 false） */
  multiple?: boolean;
  /** 允许选择的层级：任意一级 / 仅叶子（默认 'any'） */
  allowSelectLevel?: RegionSelectAllowLevel;
  /** 占位文案 */
  placeholder?: string;
  /** 类名、样式等透传 */
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

function buildTreeData(
  nodes: API.LocationTreeNode[],
  allowSelectLevel: RegionSelectAllowLevel,
  mapByCode: Map<string, { node: API.LocationTreeNode; parentChain: API.LocationTreeNode[] }>,
): TreeSelectProps['treeData'] {
  return nodes.map((node) => {
    const hasChildren = Boolean(node.children?.length);
    const selectable = allowSelectLevel === 'leaf' ? !hasChildren : true;
    const pathNodes = [...(mapByCode.get(node.code)?.parentChain ?? []), node];
    const label = getLocationDisplayName(node);
    const title = (
      <Popover
        key={node.code}
        content={<RegionPathPopoverContent pathNodes={pathNodes} />}
        trigger="hover"
        placement="right"
        mouseEnterDelay={0.2}
        mouseLeaveDelay={0.1}
        overlayStyle={{ maxWidth: 420 }}
      >
        <span>{label}</span>
      </Popover>
    );
    return {
      title,
      value: node.code,
      key: node.code,
      selectable,
      children: hasChildren && node.children
        ? buildTreeData(node.children, allowSelectLevel, mapByCode)
        : undefined,
    };
  });
}

/**
 * 地区选择组件
 *
 * 数据源：已启用国家的地区树（带缓存）
 * - 支持选到任意一级或仅叶子，由 allowSelectLevel 控制
 * - 支持单选 / 多选，由 multiple 控制
 */
const RegionSelect: React.FC<RegionSelectProps> = ({
  value,
  onChange,
  multiple = false,
  allowSelectLevel = 'any',
  placeholder = '请选择地区',
  className,
  style,
  disabled,
  ...rest
}) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof getEnabledCountryLocationData>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEnabledCountryLocationData()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  const treeData =
    data?.tree && data?.mapByCode
      ? buildTreeData(data.tree, allowSelectLevel, data.mapByCode)
      : [];

  return (
    <TreeSelect
      className={className}
      style={style}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading}
      treeData={treeData}
      treeLine
      allowClear
      showSearch
      treeNodeFilterProp="title"
      value={value}
      onChange={onChange}
      multiple={multiple}
      {...rest}
    />
  );
};

export default RegionSelect;
